import ColorName from "../../library/ColorDot/ColorName";
import { Annotation, Dot, FlowDot } from "../api/model";
import User from "../api/model/users/User";
import Color from "../model/color";
import ColorDefinition from "../model/ColorDefinition";

import {
    AIReviewCommentStatusFilter,
    AnnotationStatusFilter,
    ApprovalsCommentStatusFilter
} from "../model/RemarkStatusFilters";
import RemarkType from "../model/RemarkType";

import BasicRecord from "./BasicRecord";

import { mentionRegexFn } from "./regexFn";

const dotColorNames: BasicRecord<ColorName> = {
    "fecf33": "yellow",
    "f69833": "orange",
    "ee6723": "peach",
    "c8cf2d": "green",
    "0dcfda": "turquoise",
    "419bf9": "cornflowerBlue",
    "db7cee": "deepPurple",
    "979197": "gandalf"
};

const dotColorValues: BasicRecord<ColorDefinition> = {
    yellow: {
        name: "yellow",
        r: 254,
        g: 207,
        b: 51,
        a: 1
    },
    orange: {
        name: "orange",
        r: 246,
        g: 152,
        b: 51,
        a: 1
    },
    peach: {
        name: "peach",
        r: 238,
        g: 103,
        b: 35,
        a: 1
    },
    green: {
        name: "green",
        r: 200,
        g: 207,
        b: 45,
        a: 1
    },
    turquoise: {
        name: "turquoise",
        r: 13,
        g: 207,
        b: 218,
        a: 1
    },
    cornflowerBlue: {
        name: "cornflowerBlue",
        r: 65,
        g: 155,
        b: 249,
        a: 1
    },
    deepPurple: {
        name: "deepPurple",
        r: 219,
        g: 124,
        b: 238,
        a: 1
    },
    teflon: {
        name: "teflon",
        r: 85,
        g: 77,
        b: 86,
        a: 1
    },
    gandalf: {
        name: "gandalf",
        r: 151,
        g: 145,
        b: 151,
        a: 1
    }
};

const DOT_MAX_COORDINATE = 0.6;
const DOT_MIN_COORDINATE = 0.4;
const DOT_PRECISION = 2;

const PLACEHOLDERS = [
    "It’s a dangerous business, Frodo.",
    "Call me Ishmael.",
    "Cool cool cool.",
    "Streets ahead.",
    "It’s okay to be sleepy in the afternoon.",
    "I’m not superstitious, but I am a little stitious.",
    "You’re a wizard, Harry.",
    "They’re taking the hobbits to Isengard.",
    "Mama mia, let me go!",
    "When Paris has to pee, Paris has to pee!",
    "I have a bad feeling about this.",
    "It’s a trap!",
    "Ten thousand thundering typhoons!",
    "Billions of blue blistering barnacles!",
    "Who ordered the carrot juice?",
    "I like Kaş.",
    "Did I stutter?",
    "We’re gonna need a bigger boat.",
    "Yip yip!"
];

export const colorNames = [
    "yellow",
    "orange",
    "peach",
    "green",
    "turquoise",
    "cornflowerBlue",
    "deepPurple"
];

export function getColorName(color: string | ColorDefinition): ColorName {
    const colorHex = typeof color === "string"
        ? color
        : new Color(color).hexBase();

    return dotColorNames[colorHex];
}

export function getColorValueByName(colorName: string): ColorDefinition {
    return dotColorValues[colorName];
}

export function isMentionedDot(dot: Pick<Dot, "comments">, user: User): boolean {
    if (!dot.comments?.length) {
        return false;
    }

    const mentionRegex = mentionRegexFn(user.username!);

    return dot.comments.some(comment => (
        mentionRegex.test(comment.note)
    ));
}

export function isAuthoredByMeDot(dot: Pick<Dot, "comments">, user: User): boolean {
    if (!dot.comments?.length) {
        return false;
    }

    return dot.comments.some(comment => (
        comment.author?._id === user._id
    ));
}

export function isMentionedFlowDot(dot: FlowDot, user: User): boolean {
    if (!dot.comments?.length) {
        return false;
    }

    const mentionRegex = mentionRegexFn(user.username!);

    return dot.comments.some(comment => (
        mentionRegex.test(comment.note) || comment.createdBy?._id === user._id
    ));
}

export function isMentionedAnnotation(annotation: Annotation, user: User): boolean {
    if (!annotation.text) {
        return false;
    }

    const mentionRegex = mentionRegexFn(user._id);

    return mentionRegex.test(annotation.text);
}

export function hasImage(note: string): boolean {
    return /!\[.*\]\(.+\)/.test(note);
}

export function checkDotFilter(dot: Dot, status: string, color: string | null, user?: User): boolean {
    if (status === "none") {
        return false;
    }

    if (color && getColorName(dot.color) !== color) {
        return false;
    }

    if (status === "mentioned" && user) {
        return isMentionedDot(dot, user) || isAuthoredByMeDot(dot, user);
    }

    if (status === ApprovalsCommentStatusFilter.Pending) {
        return isApprovalPendingDot(dot.dotType!, dot._id);
    }

    if (status === AIReviewCommentStatusFilter.All) {
        return true;
    }

    return status === (dot.status || "open");
}

export function checkFlowDotFilter(dot: FlowDot, status: string, color: string | null, user: User): boolean {
    if (status === "none") {
        return false;
    }

    if (color && getColorName(dot.color) !== color) {
        return false;
    }

    if (status === "mentioned") {
        return isMentionedFlowDot(dot, user);
    }

    return status === (dot.status || "open");
}

// eslint-disable-next-line max-params
export function checkAnnotationFilter(
    annotation: Annotation,
    status: string,
    filterId: string | undefined,
    configFilter: AnnotationStatusFilter,
    _user: User
) {
    if (configFilter === "none") {
        return false;
    }

    if (status === "All" || (annotation.type && annotation.type === filterId)) {
        return configFilter === "all" ? true : isMentionedAnnotation(annotation, _user);
    }

    return false;
}

export function isDraftDot(dotId: string | null | undefined): boolean {
    return dotId?.startsWith("newDot") ?? false;
}

export function isDraftAnnotation(annotationId: string | null | undefined): boolean {
    return annotationId?.startsWith("newAnnotation") ?? false;
}

export function isDraft(daid: string | null | undefined, kind: RemarkType): boolean {
    return kind === RemarkType.Dot
        ? isDraftDot(daid)
        : isDraftAnnotation(daid);
}

export function isApprovalPendingDot(
    dotType: RemarkType,
    dotId: string | null | undefined
) {
    if (dotType === RemarkType.ApprovalNote && isDraftDot(dotId)) {
        return true;
    }

    return dotId?.startsWith("approvalsPending") ?? false;
}

export function getRandomPlaceholder(): string {
    const seed = Math.floor(Math.random() * 100); // eslint-disable-line no-magic-numbers

    // with 97% probability, display the regular placeholder message
    if (seed > 2) { // eslint-disable-line no-magic-numbers
        return "Type something";
    }

    // with 3% probability, display a fun random placeholder message
    return PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)];
}

export function getRandomDotCoordinate(): number {
    return parseFloat(
        (
            Math.random() *
            (DOT_MAX_COORDINATE - DOT_MIN_COORDINATE) +
            DOT_MIN_COORDINATE
        ).toFixed(DOT_PRECISION)
    );
}
